import { ChangeEvent, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import Cohort from '@/models/cohort';
import { useCohorts } from '@/services/cohorts';
import CohortModal from './components/cohort-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';

const valueFor = (row: Cohort, key: string): string | number => {
    switch (key) {
        case 'capacity':
            return row.attendeeCapacity ?? -1;
        case 'sessions':
            return row.liveSessions?.length ?? 0;
        default: {
            const v = (row as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    return (a: Cohort, b: Cohort) => {
        const av = valueFor(a, orderBy);
        const bv = valueFor(b, orderBy);
        if (av < bv) return order === 'desc' ? 1 : -1;
        if (av > bv) return order === 'desc' ? -1 : 1;
        return 0;
    };
};

const statusLabel = (c: Cohort): string => c.status.charAt(0).toUpperCase() + c.status.slice(1);
const capacityLabel = (c: Cohort): string => `${c.currentAttendeeCount ?? 0}/${c.attendeeCapacity}`;

const Cohorts = () => {
    const { data: cohorts, isLoading } = useCohorts();
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('startDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCohortId, setSelectedCohortId] = useState<string>();

    // Derived (not state) so a background refetch of ['cohorts'] never wipes the active
    // search — the list is always cohorts.data re-filtered by the current search term.
    const filtered = useMemo(() => {
        const rows = cohorts?.data ?? [];
        const q = search.trim().toLowerCase();
        return q ? rows.filter((c) => c.title.toLowerCase().includes(q)) : rows;
    }, [cohorts, search]);

    // Clamp so a mutation/search that shrinks the list never strands the user on
    // an out-of-range (empty) page — without surprising them with a reset on a
    // benign background refetch.
    const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));

    const toggle = () => setIsOpen((prev) => !prev);

    const handleRequestAdd = () => {
        setSelectedCohortId(undefined);
        toggle();
    };

    const handleRequestSort = (_: unknown, property: string) => {
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }
        setOrder(orderTemp);
        setOrderBy(property);
    };

    const handleClick = (id: string) => {
        setSelectedCohortId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Cohorts</h3>
                    <h3 className="page-subhead subhead">Scheduled runs of the 33 course with live sessions.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Title"
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filtered
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
                                                    .map((d) => (
                                                        <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {d.title}
                                                            </TableCell>
                                                            <TableCell>{d.startDate}</TableCell>
                                                            <TableCell>{statusLabel(d)}</TableCell>
                                                            <TableCell>{capacityLabel(d)}</TableCell>
                                                            <TableCell>{d.liveSessions?.length ?? 0}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filtered.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center">
                                                            No cohorts yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filtered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={safePage}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <CohortModal isOpen={isOpen} toggle={toggle} selectedCohortId={selectedCohortId} />
        </Container>
    );
};

export default Cohorts;
